/**
 * TranscriptsListPage — list all transcribed calls with status indicators.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import TranscriptPage from './TranscriptPage';

const API = '/api/ai';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

interface TranscriptRow {
  id: number;
  recording_id: number;
  language: string;
  word_count: number;
  duration_seconds: number;
  whisper_model: string;
  created_at: string;
  preview: string;
  filename: string;
  original_name: string;
  recorded_at: string;
  username: string;
  job_status: string;
}

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  completed:  { bg: '#10b98122', color: '#10b981', label: 'Completed' },
  pending:    { bg: '#f59e0b22', color: '#f59e0b', label: 'Pending' },
  processing: { bg: '#6366f122', color: '#6366f1', label: 'Processing' },
  failed:     { bg: '#ef444422', color: '#ef4444', label: 'Failed' },
  skipped:    { bg: '#64748b22', color: '#64748b', label: 'Skipped' },
};

export default function TranscriptsListPage() {
  const [items, setItems] = useState<TranscriptRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState('created_at_desc');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);

  const load = useCallback(async (pg = 1, srt = sort) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/transcripts`, {
        params: { page: pg, page_size: 25, sort: srt },
        ...auth(),
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
      setPage(pg);
    } catch {}
    finally { setLoading(false); }
  }, [sort]);

  useEffect(() => { load(1, sort); }, [sort]);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (selected !== null) {
    return <TranscriptPage recordingId={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', background: '#0f172a', color: '#e2e8f0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#f1f5f9' }}>📝 Call Transcripts</h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{total} transcripts</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={sort} onChange={e => setSort(e.target.value)} style={{
            background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
            borderRadius: 8, padding: '7px 12px', fontSize: 13,
          }}>
            <option value="created_at_desc">Newest First</option>
            <option value="created_at_asc">Oldest First</option>
            <option value="word_count_desc">Most Words</option>
          </select>
          <button onClick={() => load(page, sort)} style={{
            background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
            borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer',
          }}>↺ Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>⏳ Loading transcripts…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#334155' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎙</div>
          <div style={{ fontSize: 16, color: '#475569', fontWeight: 600 }}>No transcripts yet</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 8, lineHeight: 1.6 }}>
            Enable AI Transcription in the Transcription Admin panel,<br />
            then upload or make calls to begin generating transcripts.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => {
            const badge = STATUS_BADGE[item.job_status] || STATUS_BADGE.completed;
            return (
              <div
                key={item.id}
                onClick={() => setSelected(item.recording_id)}
                style={{
                  background: '#1e293b', borderRadius: 10, padding: '14px 18px',
                  cursor: 'pointer', border: '1px solid #334155', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#6366f1')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#334155')}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>
                        📞 {item.original_name || item.filename}
                      </div>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: badge.bg, color: badge.color, fontWeight: 700 }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      👤 {item.username} · 📅 {new Date(item.recorded_at || item.created_at).toLocaleString()}
                      {item.word_count && ` · 📝 ${item.word_count} words`}
                      {item.duration_seconds && ` · ⏱ ${formatDuration(Number(item.duration_seconds))}`}
                      {item.language && ` · 🌐 ${item.language.toUpperCase()}`}
                    </div>
                    {item.preview && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        "{item.preview}…"
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    View →
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          <button onClick={() => load(page - 1, sort)} disabled={page <= 1} style={pagBtn}>◀ Prev</button>
          <span style={{ padding: '6px 12px', color: '#64748b', fontSize: 13 }}>{page} / {pages}</span>
          <button onClick={() => load(page + 1, sort)} disabled={page >= pages} style={pagBtn}>Next ▶</button>
        </div>
      )}
    </div>
  );
}

const pagBtn: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
  borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer',
};
